import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-xl text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground border border-gold/30 hover:border-gold/60 hover:bg-primary/90 hover:shadow-lg hover:shadow-gold/20",
        destructive: "bg-destructive text-destructive-foreground border border-destructive/30 hover:bg-destructive/90",
        outline: "border-2 border-gold/40 bg-background hover:bg-gold/10 hover:border-gold/70 hover:text-primary",
        secondary: "bg-secondary text-secondary-foreground border border-gold/20 hover:border-gold/40 hover:bg-secondary/80",
        ghost: "hover:bg-gold/10 hover:text-primary",
        link: "text-primary underline-offset-4 hover:underline",
        premium: "bg-gradient-to-r from-gold-dark via-gold to-gold-light text-white font-semibold border border-gold-light/50 hover:shadow-xl hover:shadow-gold/40 hover:scale-[1.02]",
        premiumOutline: "border-2 border-gold bg-transparent text-gold-dark hover:bg-gold/15 hover:border-gold-light font-semibold hover:shadow-lg hover:shadow-gold/20",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-xl px-3",
        lg: "h-11 rounded-xl px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
